"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import { DEMO_PROFILES, PROFILE_DIMENSIONS } from "./content";
import { trackLandingEvent } from "./analytics";

function Avatar({ image, name }: { image: string; name: string }) {
  return <div className="demo-avatar"><Image src={image} alt={`Portrait used for the fictional ${name} demonstration profile`} fill sizes="(max-width: 640px) 50vw, 320px" /></div>;
}

function ActionIcon({ name }: { name: "pass" | "details" | "like" }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {name === "pass" ? <><path d="m6 6 12 12" /><path d="M18 6 6 18" /></> : null}
    {name === "details" ? <><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8h.01" /></> : null}
    {name === "like" ? <path d="M20.8 5.7a5.5 5.5 0 0 0-7.8 0L12 6.8l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 22l8.8-8.5a5.5 5.5 0 0 0 0-7.8Z" /> : null}
  </svg>;
}

export function BeyondThePhoto() {
  return (
    <section id="why" className="landing-section beyond-section" aria-labelledby="beyond-title">
      <div className="scene-inner split-layout">
        <div className="section-copy">
          <p className="landing-kicker">Beyond the photograph</p>
          <h2 id="beyond-title">A first impression is only the beginning.</h2>
          <p>Attraction matters. So do intention, faith, values, character and the kind of life you hope to build.</p>
        </div>
        <div className="profile-context" aria-label="A fictional profile shown alongside the relationship context someone chooses to share">
          <div className="context-portrait"><Avatar image="/images/landing/amara-800.webp" name="Amara" /><div><strong>Amara, 31</strong><span>London</span></div></div>
          <div className="context-details">
            <p>What matters to Amara</p>
            <ul>
              {PROFILE_DIMENSIONS.map((label) => <li key={label}>{label}</li>)}
            </ul>
            <span>Shared by each person, on their terms.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CompatibilityExperience({ onComplete }: { onComplete: () => void }) {
  const [proximity, setProximity] = useState(36);
  const tracked = useRef(false);
  const complete = proximity >= 46;
  const setAligned = (next: number) => {
    setProximity(next);
    if (next >= 46 && !tracked.current) {
      tracked.current = true;
      onComplete();
      trackLandingEvent("compatibility_interaction_completed");
    }
  };
  const respondToPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "touch") return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const centreDistance = Math.abs(event.clientX - (bounds.left + bounds.width / 2)) / (bounds.width / 2);
    setAligned(48 - Math.min(1, centreDistance) * 16);
  };
  return (
    <section id="compatibility" className="landing-section compatibility-section" aria-labelledby="compatibility-title">
      <div className="scene-inner">
        <div className="section-heading centered">
          <p className="landing-kicker">Why you may connect</p>
          <h2 id="compatibility-title">See where your lives may align.</h2>
          <p>Bring the profiles together to reveal shared themes and the conversations still worth having.</p>
        </div>
        <div className={`compatibility-stage ${complete ? "is-aligned" : ""}`} style={{ "--profile-gap": `${proximity}%` } as React.CSSProperties} onPointerMove={respondToPointer} onClick={() => setAligned(48)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setAligned(complete ? 36 : 48); } }} role="button" tabIndex={0} aria-pressed={complete} aria-label={complete ? "Profiles aligned" : "Bring demonstration profiles together"}>
          <article className="compat-profile first"><Avatar image="/images/landing/amara-800.webp" name="Amara" /><h3>Amara</h3><p>Family closeness · Marriage · Direct communicator</p></article>
          <div className="compatibility-bridge" aria-hidden="true"><i /><p><span>Shared direction</span><strong>Commitment</strong></p><i /></div>
          <div className="love-burst" aria-hidden="true"><span>♥</span><i /><i /><i /><i /><i /><i /></div>
          <article className="compat-profile second"><Avatar image="/images/landing/daniel-800.webp" name="Daniel" /><h3>Daniel</h3><p>Commitment · Independence · Thoughtful pace</p></article>
        </div>
        <p className="compatibility-caption">Compatibility is context, not certainty. The decision remains yours. This is a fictional demonstration.</p>
      </div>
    </section>
  );
}

export function EncounterDemo({ onMatch, sound }: { onMatch: () => void; sound: boolean }) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<"pass" | "like" | null>(null);
  const [details, setDetails] = useState(false);
  const startX = useRef<number | null>(null);
  const matched = index >= 2;
  const decide = (kind: "pass" | "like") => {
    if (direction || matched) return;
    setDirection(kind);
    trackLandingEvent("encounter_demo_used");
    setTimeout(() => {
      const next = index + 1;
      setIndex(next); setDirection(null); setDetails(false);
      if (kind === "like" && next >= 2) onMatch();
    }, 420);
  };
  const pointerUp = (event: React.PointerEvent) => {
    if (startX.current == null) return;
    const delta = event.clientX - startX.current;
    startX.current = null;
    if (Math.abs(delta) > 60) decide(delta > 0 ? "like" : "pass");
  };
  return (
    <section id="encounters" className="landing-section encounter-section" aria-labelledby="encounter-title">
      <div className="scene-inner split-layout encounter-layout">
        <div className="section-copy">
          <p className="landing-kicker">A considered Encounter deck</p>
          <h2 id="encounter-title">Choose with context, not momentum.</h2>
          <p>Each demonstration card already has another thoughtful introduction waiting behind it. Pass, explore details, or express interest.</p>
          <p className="narrative-fragment">“Attraction got us talking. Alignment kept us talking.” <span>Brand narrative</span></p>
        </div>
        <div className="encounter-demo">
          {!matched ? (
            <>
              <div className="deck-stack" aria-live="polite">
                {[2, 1, 0].map((offset) => {
                  const profile = DEMO_PROFILES[index + offset];
                  if (!profile) return null;
                  const top = offset === 0;
                  return <article key={`${index}-${offset}`} className={`encounter-card layer-${offset} ${top && direction ? `is-${direction}` : ""}`} onPointerDown={top ? (e) => { startX.current = e.clientX; e.currentTarget.setPointerCapture(e.pointerId); } : undefined} onPointerUp={top ? pointerUp : undefined}>
                    <Avatar image={profile.image} name={profile.name} />
                    <div className="card-scrim" />
                    <div className="card-copy"><h3>{profile.name}, {profile.age}</h3><p>{profile.place} · {profile.note}</p><p className="profile-prompt">“{profile.prompt}”</p>{details && top ? <><strong className="goal-label">Looking for · {profile.goal}</strong><ul>{profile.values.map((value) => <li key={value}>{value}</li>)}</ul></> : null}</div>
                  </article>;
                })}
              </div>
              <div className="deck-actions">
                <button type="button" aria-label="Pass" onClick={() => decide("pass")}><ActionIcon name="pass" /></button>
                <button type="button" aria-label="View details" onClick={() => setDetails((value) => !value)} aria-expanded={details}><ActionIcon name="details" /></button>
                <button type="button" aria-label="Like" onClick={() => decide("like")}><ActionIcon name="like" /></button>
              </div>
            </>
          ) : (
            <div className="match-moment" role="status">
              <div className="match-faces"><Avatar image="/images/landing/amara-800.webp" name="Amara" /><span aria-hidden="true">—</span><Avatar image="/images/landing/daniel-800.webp" name="Daniel" /></div>
              <p className="landing-kicker">A mutual choice, demonstrated</p><h3>You both chose with intention.</h3>
              <p>Start with something that already matters to both of you.</p>
              {sound ? <span className="sound-lived" aria-label="A soft match tone played">Sound moment active</span> : null}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
